/**
 * JL Subscription Core Module - Production Ready
 * Complete vanilla JS conversion of keto-blast-js.js
 *
 * @version 2.0.0
 * @requires window.JL - JL Utilities library
 * @author Claude Code
 *
 * CRITICAL FEATURES:
 * - URL parameter handling with duplicate question mark fix
 * - Product box selection with variant switching
 * - Tab switching (IIFE pattern) for one-time vs subscription
 * - One-time cart add (no selling_plan)
 * - Subscription cart add (WITH selling_plan - CRITICAL for Recharge)
 * - Cart fetch/render/clear functionality
 * - Modal handling for cart confirmations
 * - Sold out state initialization
 * - Image thumbnail synchronization
 * - URL history management
 */

(function(global) {
  'use strict';

  // ============================================
  // DEPENDENCY CHECK
  // ============================================

  if (!global.JL) {
    console.error('[JL-CORE] JL utilities library not found. Module cannot initialize.');
    return;
  }

  // Initialization guard - prevent duplicate handlers
  if (global.JLSubscriptionCore && global.JLSubscriptionCore._initialized) {
    console.log('[JL-CORE] Module already initialized, skipping');
    return;
  }

  const { $, $$, addClass, removeClass, hasClass, hide, show, setText, setHtml, getAttr, setAttr, removeAttr, val, on } = global.JL;

  console.log('[JL-CORE] Loading subscription core module...');

  // ============================================
  // DOM ELEMENT CACHE
  // ============================================

  let elements = {
    // Modal elements
    cartDrawerContent: null,
    ebModal: null,
    ebSpan: null,

    // Selling plan input (CRITICAL)
    sellingPlanInput: null,

    // Price display
    actualPrice: null,
    comparePrice: null,
    offerName: null,

    // Cart count
    cartCount: null
  };

  // ============================================
  // URL SANITIZATION
  // ============================================

  /**
   * Fix duplicate question marks in URL
   * Replaces additional ? with & to maintain valid URL structure
   */
  function sanitizeUrl() {
    console.log('[JL-CORE] Sanitizing URL...');

    const currentUrl = window.location.href;
    const questionMarkCount = (currentUrl.match(/\?/g) || []).length;

    if (questionMarkCount > 1) {
      console.log('[JL-CORE] Found multiple question marks, fixing URL');

      const indexOfLastQuestionMark = currentUrl.lastIndexOf('?');
      const modifiedUrl = currentUrl.substring(0, indexOfLastQuestionMark) +
                          '&' +
                          currentUrl.substring(indexOfLastQuestionMark + 1);

      window.history.replaceState(null, null, modifiedUrl);
      console.log('[JL-CORE] Modified URL:', modifiedUrl);
    }
  }

  // ============================================
  // URL PARAMETER HELPERS
  // ============================================

  /**
   * Get URL parameters (vanilla JS version of getUrlVars)
   * @returns {Object} URL parameters as key-value pairs
   */
  function getUrlVars() {
    const vars = {};
    const hashes = window.location.href.slice(window.location.href.indexOf('?') + 1).split('&');

    for (let i = 0; i < hashes.length; i++) {
      const hash = hashes[i].split('=');
      vars[hash[0]] = hash[1];
    }

    return vars;
  }

  /**
   * Update URL with new variant parameter
   * @param {string|number} variantId - Variant ID to set in URL
   */
  function changeurl(variantId) {
    console.log('[JL-CORE] Changing URL to variant:', variantId);

    let url = window.location.href;
    const field = 'variant';

    if (url.indexOf('?' + field + '=') !== -1) {
      console.log('[JL-CORE] Found ? variant parameter');
      const newUrl = url.replace(/(variant=)[^\&]+/, '$1' + variantId);
      window.history.pushState("data", "", newUrl);
    } else if (url.indexOf('&' + field + '=') !== -1) {
      console.log('[JL-CORE] Found & variant parameter');
      const newUrl = url.replace(/(variant=)[^\&]+/, '$1' + variantId);
      window.history.pushState("data", "", newUrl);
    } else {
      console.log('[JL-CORE] No variant in URL, adding it');

      if (url.indexOf('?') !== -1) {
        console.log('[JL-CORE] URL has query parameters, appending with &');
        const newUrl = url + '&variant=' + variantId;
        window.history.pushState("data", "", newUrl);
      } else {
        console.log('[JL-CORE] URL has no query parameters, appending with ?');
        const newUrl = url + '?variant=' + variantId;
        window.history.pushState("data", "", newUrl);
      }
    }
  }

  // ============================================
  // PRICE UPDATE HELPER
  // ============================================

  /**
   * Update price display elements
   * @param {string|null} actualPrc - Actual price value
   * @param {string|null} cmpPrc - Compare at price value
   */
  function updatePriceDisplay(actualPrc, cmpPrc) {
    console.log('[JL-CORE] Updating price display:', { actualPrc, cmpPrc });

    // Update actual price
    if (actualPrc && actualPrc.trim() !== '') {
      setText('.actual_prc', '$' + actualPrc);
    } else {
      setText('.actual_prc', 'Out Of Stock');
    }

    // Update compare price
    if (cmpPrc && cmpPrc.trim() !== '') {
      setText('.cmpr-prc', '$' + cmpPrc);
    } else {
      setText('.cmpr-prc', '');
    }
  }

  /**
   * Update product name display
   * @param {string} prodName - Product name
   */
  function updateProductName(prodName) {
    if (!prodName) return;

    console.log('[JL-CORE] Updating product name:', prodName);
    setText('#offer_name', prodName);
  }

  // ============================================
  // VARIANT URL PARAMETER HANDLER
  // ============================================

  /**
   * Handle variant parameter in URL on page load
   * Sets up the correct product selection and tab based on URL
   */
  function handleUrlVariantParameter() {
    console.log('[JL-CORE] Checking for variant URL parameter...');

    const params = new URLSearchParams(window.location.search);

    if (params.has('variant')) {
      console.log('[JL-CORE] Variant parameter found in URL');

      const getVariant = getUrlVars()["variant"];
      console.log('[JL-CORE] Current variant:', getVariant);

      // Remove selected_box from all product boxes
      $$('.product_box_area').forEach(box => removeClass(box, 'selected_box'));

      // Find the element with the variant class
      const variantElement = $('.' + getVariant);
      if (!variantElement) {
        console.warn('[JL-CORE] Variant element not found:', getVariant);
        updateDefaultSelection();
        return;
      }

      // Get product number and select all matching boxes
      const productNo = getAttr(variantElement, 'prod-no');
      if (productNo) {
        $$('.' + productNo).forEach(box => addClass(box, 'selected_box'));
      }

      // Determine product type (one_time or subscription)
      const productType = getAttr(variantElement, 'prod-type');
      console.log('[JL-CORE] Product type:', productType);

      if (productType === 'one_time') {
        switchToOneTime();
      } else {
        switchToSubscription();
      }

      // Update price display
      const actualPrc = getAttr(variantElement, 'actual-prc');
      const cmpPrc = getAttr(variantElement, 'cmp-prc');
      const prodName = getAttr(variantElement, 'prod-name');

      updatePriceDisplay(actualPrc, cmpPrc);
      updateProductName(prodName);

    } else {
      console.log('[JL-CORE] No variant parameter, using default selection');
      updateDefaultSelection();
    }
  }

  /**
   * Update display for default selected box
   */
  function updateDefaultSelection() {
    const selectedBox = $('.selected_box');
    if (!selectedBox) {
      console.warn('[JL-CORE] No selected box found');
      return;
    }

    const actualPrc = getAttr(selectedBox, 'actual-prc');
    const cmpPrc = getAttr(selectedBox, 'cmp-prc');
    const prodName = getAttr(selectedBox, 'prod-name');

    updatePriceDisplay(actualPrc, cmpPrc);
    updateProductName(prodName);
  }

  // ============================================
  // TAB SWITCHING (ONE-TIME vs SUBSCRIPTION)
  // ============================================

  /**
   * Switch to one-time purchase mode
   */
  function switchToOneTime() {
    console.log('[JL-CORE] Switching to one-time purchase');

    // Update tabs
    $$('.listing_link').forEach(link => removeClass(link, 'active_tab'));
    const oneTimeTab = $('.onetime_');
    if (oneTimeTab) addClass(oneTimeTab, 'active_tab');

    // Hide all product tabs, show one-time area
    hide('.prdct_tab');
    show('.one_time_purchase_area');

    // Show one-time cart area, hide subscription area
    hide('.sub_cart_area');
    show('.one_time_cart_area');

    // Update button IDs (for legacy compatibility)
    const subCartBtn = $('.sub_cart_btn');
    const oneTimeCartBtn = $('.one_time_cart_btn');

    if (subCartBtn) removeAttr(subCartBtn, 'id');
    if (oneTimeCartBtn) setAttr(oneTimeCartBtn, 'id', 'new-form-atc');

    // Remove selling_plan name attribute (CRITICAL)
    const sellingPlanInput = $('#subs_seller_planId');
    if (sellingPlanInput) {
      removeAttr(sellingPlanInput, 'name');
      console.log('[JL-CORE] Removed selling_plan attribute for one-time purchase');
    }

    // Update option selector
    const optionSelector = $('#SingleOptionSelector-0');
    if (optionSelector) val(optionSelector, 'One Time');

    // Update prices from selected one-time box
    updatePricesForActiveTab('onetime');
  }

  /**
   * Switch to subscription mode
   */
  function switchToSubscription() {
    console.log('[JL-CORE] Switching to subscription');

    // Update tabs
    $$('.listing_link').forEach(link => removeClass(link, 'active_tab'));
    const subsTab = $('.subs_');
    if (subsTab) addClass(subsTab, 'active_tab');

    // Hide all product tabs, show subscription area
    hide('.prdct_tab');
    show('.save_and_subscribe_area');

    // Show subscription cart area, hide one-time area
    hide('.one_time_cart_area');
    show('.sub_cart_area');

    // Update button IDs (for legacy compatibility)
    const oneTimeCartBtn = $('.one_time_cart_btn');
    const subCartBtn = $('.sub_cart_btn');

    if (oneTimeCartBtn) removeAttr(oneTimeCartBtn, 'id');
    if (subCartBtn) setAttr(subCartBtn, 'id', 'new-form-atc');

    // Add selling_plan name attribute (CRITICAL for Recharge)
    const sellingPlanInput = $('#subs_seller_planId');
    if (sellingPlanInput) {
      setAttr(sellingPlanInput, 'name', 'selling_plan');
      console.log('[JL-CORE] Added selling_plan attribute for subscription');
    }

    // Update option selector
    const optionSelector = $('#SingleOptionSelector-0');
    if (optionSelector) val(optionSelector, 'Subscription');

    // Update prices from selected subscription box
    updatePricesForActiveTab('subs');
  }

  /**
   * Update prices for the active tab (one-time or subscription)
   * @param {string} type - 'onetime' or 'subs'
   */
  function updatePricesForActiveTab(type) {
    console.log('[JL-CORE] Updating prices for type:', type);

    const selector = '.' + type;
    const typeElement = $(selector);

    if (!typeElement) {
      console.log('[JL-CORE] No ' + type + ' element found, showing out of stock');
      setText('.actual_prc', 'Out Of Stock');
      setText('.cmpr-prc', '');
      return;
    }

    // Find selected box within this type
    const selectedBox = $(selector + '.selected_box');
    if (!selectedBox) {
      console.warn('[JL-CORE] No selected box found for type:', type);
      return;
    }

    const actualPrc = getAttr(selectedBox, 'actual-prc');
    const cmpPrc = getAttr(selectedBox, 'cmp-prc');
    const variantId = getAttr(selectedBox, 'variant-id');

    updatePriceDisplay(actualPrc, cmpPrc);

    if (variantId) {
      changeurl(variantId);

      // Update dropdown selector
      const selectOption = $(`.abcd option[value="${variantId}"]`);
      if (selectOption) {
        selectOption.selected = true;
      }
    }
  }

  // ============================================
  // PRODUCT BOX CLICK HANDLER
  // ============================================

  /**
   * Bind click handlers to product selection boxes
   */
  function bindProductBoxHandlers() {
    console.log('[JL-CORE] Binding product box handlers');

    on('.product_box_area', 'click', function(e) {
      const clickedBox = e.currentTarget;
      console.log('[JL-CORE] Product box clicked');

      // Remove selection from all boxes
      $$('.product_box_area').forEach(box => removeClass(box, 'selected_box'));

      // Get product number and select all matching boxes
      const prodNo = getAttr(clickedBox, 'prod-no');
      if (prodNo) {
        $$('.' + prodNo).forEach(box => addClass(box, 'selected_box'));
      }

      // Update price display
      const actualPrice = getAttr(clickedBox, 'actual-prc');
      const compPrc = getAttr(clickedBox, 'cmp-prc');
      updatePriceDisplay(actualPrice, compPrc);

      // Update product name
      const prodName = getAttr(clickedBox, 'prod-name');
      if (prodName) {
        updateProductName(prodName);

        const optionSelector = $('#SingleOptionSelector-1');
        if (optionSelector) val(optionSelector, prodName);
      }

      // Update product image
      updateProductImage(clickedBox);

      // Update URL with variant
      const variantId = getAttr(clickedBox, 'variant-id');
      if (variantId) {
        changeurl(variantId);

        // Update variant dropdown
        const selectOption = $(`.abcd option[value="${variantId}"]`);
        if (selectOption) {
          selectOption.selected = true;
        }
      }
    });
  }

  /**
   * Update product image thumbnails and main images
   * @param {Element} box - Product box element
   */
  function updateProductImage(box) {
    const varImage = getAttr(box, 'img-id');
    if (!varImage) return;

    console.log('[JL-CORE] Updating product image:', varImage);

    // Update thumbnails
    $$('.product-single__thumbnail').forEach(thumb => {
      removeClass(thumb, 'active-thumb');
      const imgSrc = getAttr(thumb, 'data-thumbnail-id');
      if (imgSrc === varImage) {
        addClass(thumb, 'active-thumb');
      }
    });

    // Update main product photos
    $$('.product-single__photo--has-thumbnails').forEach(photo => {
      const zoomImgSrc = getAttr(photo, 'data-image-id');
      if (zoomImgSrc === varImage) {
        removeClass(photo, 'hide');
      } else {
        addClass(photo, 'hide');
      }
    });
  }

  // ============================================
  // TAB LINK HANDLERS (IIFE PATTERN)
  // ============================================

  /**
   * Bind tab link handlers using IIFE pattern
   * Handles switching between one-time and subscription tabs
   */
  function bindTabLinkHandlers() {
    console.log('[JL-CORE] Binding tab link handlers (IIFE pattern)');

    (function() {
      on('.listing_link', 'click', function(e) {
        e.preventDefault();

        const tab = e.currentTarget;
        const tabTitle = getAttr(tab, 'title');
        const purchaseType = getAttr(tab, 'data-val');

        console.log('[JL-CORE] Tab clicked:', purchaseType);

        // Hide all tabs
        hide('.prdct_tab');

        // Show selected tab
        if (tabTitle) {
          show('#' + tabTitle);
        }

        // Update active tab
        $$('.listing_link').forEach(link => removeClass(link, 'active_tab'));
        addClass(tab, 'active_tab');

        // Switch based on purchase type
        if (purchaseType === 'one-time') {
          switchToOneTime();
        } else {
          switchToSubscription();
        }

        // Store in localStorage (legacy compatibility)
        // Wrapped in try/catch for private browsing mode
        try {
          const sellerId = val($('.seller_plan'));
          if (sellerId) {
            localStorage.setItem("product_type", purchaseType);
            localStorage.setItem("seller_id", sellerId);
          }
        } catch (e) {
          console.warn('[JL-CORE] localStorage not available:', e.message);
        }
      });
    })();
  }

  // ============================================
  // CART BUTTON HANDLERS
  // ============================================

  /**
   * Bind one-time purchase cart button handler
   */
  function bindOneTimeCartButton() {
    console.log('[JL-CORE] Binding one-time cart button');

    on('.one_time_cart_btn', 'click', async function(e) {
      e.preventDefault();
      console.log('[JL-CORE] One-time cart button clicked');

      // Find selected one-time variant
      let onetimeVariantId = null;

      $$('.onetime').forEach(box => {
        if (hasClass(box, 'selected_box')) {
          onetimeVariantId = getAttr(box, 'variant-id');
        }
      });

      if (!onetimeVariantId) {
        console.error('[JL-CORE] No one-time variant selected');
        return;
      }

      console.log('[JL-CORE] Adding one-time variant to cart:', onetimeVariantId);

      // Prepare data for cart add (NO selling_plan)
      const data = {
        "id": onetimeVariantId,
        "quantity": 1
      };

      try {
        // Make AJAX request to add to cart
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data)
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        const result = await response.json();
        console.log('[JL-CORE] One-time product added to cart:', result);

        // Fetch and render cart
        await fetchCart(onetimeVariantId);

        // Show modal
        showCartModal();

      } catch (error) {
        console.error('[JL-CORE] Error adding one-time to cart:', error);
        alert('Error adding product to cart. Please try again.');
      }
    });
  }

  /**
   * Bind subscription cart button handler
   * CRITICAL: Includes selling_plan parameter for Recharge
   */
  function bindSubscriptionCartButton() {
    console.log('[JL-CORE] Binding subscription cart button');

    on('.sub_cart_btn', 'click', async function(e) {
      e.preventDefault();
      console.log('[JL-CORE] Subscription cart button clicked');

      // Get selling plan ID (CRITICAL)
      const sellingPlanInput = $('#subs_seller_planId');
      const sellingPlan = sellingPlanInput ? val(sellingPlanInput) : null;

      if (!sellingPlan) {
        console.error('[JL-CORE] No selling plan ID found');
        return;
      }

      console.log('[JL-CORE] Selling plan:', sellingPlan);

      // Find selected subscription variant
      let subVariantId = null;

      $$('.subs').forEach(box => {
        if (hasClass(box, 'selected_box')) {
          subVariantId = getAttr(box, 'variant-id');
        }
      });

      if (!subVariantId) {
        console.error('[JL-CORE] No subscription variant selected');
        return;
      }

      console.log('[JL-CORE] Adding subscription variant to cart:', subVariantId);

      // Prepare data for cart add (WITH selling_plan - CRITICAL)
      const data = {
        "id": subVariantId,
        "quantity": 1,
        "selling_plan": sellingPlan
      };

      console.log('[JL-CORE] Cart add data:', data);

      try {
        // Make AJAX request to add to cart
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data)
        });

        if (!response.ok) {
          throw new Error('Failed to add subscription to cart');
        }

        const result = await response.json();
        console.log('[JL-CORE] Subscription product added to cart:', result);

        // Fetch and render cart
        await fetchCart(subVariantId);

        // Show modal
        showCartModal();

      } catch (error) {
        console.error('[JL-CORE] Error adding subscription to cart:', error);
        alert('Error adding subscription to cart. Please try again.');
      }
    });
  }

  // ============================================
  // CART OPERATIONS
  // ============================================

  /**
   * Fetch current cart from Shopify
   * @param {string|number} vid - Variant ID that was just added
   */
  async function fetchCart(vid) {
    console.log('[JL-CORE] Fetching cart for variant:', vid);

    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }

      const cart = await response.json();
      console.log('[JL-CORE] Cart fetched:', cart);

      if (cart.item_count === 0) {
        const cartDrawerContent = $('.ebcf_line_items');
        if (cartDrawerContent) {
          cartDrawerContent.innerHTML = 'Cart is empty';
        }
      } else {
        renderCart(cart, vid);

        // Update cart count
        const cartCountSpan = $('#CartCount span');
        if (cartCountSpan) {
          setText(cartCountSpan, cart.item_count.toString());
        }
      }

    } catch (error) {
      console.error('[JL-CORE] Error fetching cart:', error);
    }
  }

  /**
   * Render cart contents in modal
   * @param {Object} cart - Cart object from Shopify
   * @param {string|number} vid - Variant ID that was just added
   */
  function renderCart(cart, vid) {
    console.log('[JL-CORE] Rendering cart for variant:', vid);
    console.log('[JL-CORE] Cart items:', cart.items);

    clearCartDrawer();

    let html = '';

    cart.items.forEach((item, index) => {
      // Extract quantity from variant title (format: "Title / Quantity")
      const qty = item.variant_title ? item.variant_title.split("/") : [];

      // Only show the item we just added
      if (vid == item.variant_id) {
        html += `
          <div class="cart_content_popup">
            <div class="cart-msg">
              <span>
                <svg class="icon icon-checkmark color-foreground-text" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 9" fill="none">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M11.35.643a.5.5 0 01.006.707l-6.77 6.886a.5.5 0 01-.719-.006L.638 4.845a.5.5 0 11.724-.69l2.872 3.011 6.41-6.517a.5.5 0 01.707-.006h-.001z" fill="currentColor"></path>
                </svg>
              </span>
              Item added to your cart
            </div>
            <div class="ovrflow_crt">
              <div class="cart-item">
                <div class="product_image_cart">
                  <img src="${item.image}" alt="">
                </div>
                <div class="cntnt_qty_both">
                  <div class="content">${item.product_title}</div>
                  <div class="qty">Quantity: ${qty.length > 1 ? qty[1] : item.quantity}</div>
                </div>
              </div>
            </div>
            <div class="actions">
              <a class="btn text-cart" href="/cart">
                <i class="icon-basket"></i>View My Cart (${cart.item_count})
              </a>
              <a class="btn" href="/checkout">
                <i class="icon-check"></i>Checkout
              </a>
              <button type="button" class="link button-label cont_shop">Continue shopping</button>
            </div>
          </div>
        `;
      }
    });

    const cartDrawerContent = $('.ebcf_line_items');
    if (cartDrawerContent) {
      cartDrawerContent.innerHTML = cartDrawerContent.innerHTML + html;
    }
  }

  /**
   * Clear cart drawer contents
   */
  function clearCartDrawer() {
    console.log('[JL-CORE] Clearing cart drawer');
    const cartDrawerContent = $('.ebcf_line_items');
    if (cartDrawerContent) {
      cartDrawerContent.innerHTML = '';
    }
  }

  // ============================================
  // MODAL HANDLERS
  // ============================================

  /**
   * Show cart modal
   */
  function showCartModal() {
    console.log('[JL-CORE] Showing cart modal');

    const modal = $('#mySizeChartModal');
    if (modal) {
      addClass(modal, 'active-modal');
      modal.style.display = 'block';
    }
  }

  /**
   * Close cart modal
   */
  function closeCartModal() {
    console.log('[JL-CORE] Closing cart modal');

    const modal = $('#mySizeChartModal');
    if (modal) {
      removeClass(modal, 'active-modal');
      modal.style.display = 'none';
    }
  }

  /**
   * Bind modal close handlers
   */
  function bindModalHandlers() {
    console.log('[JL-CORE] Binding modal handlers');

    // Close button
    const ebSpan = $('.ebcf_close');
    if (ebSpan) {
      ebSpan.onclick = function() {
        closeCartModal();
      };
    }

    // Continue shopping button (delegated event)
    on(document, 'click', function(e) {
      if (e.target.matches('.cont_shop') || e.target.closest('.cont_shop')) {
        closeCartModal();
      }
    });

    // Click outside modal
    window.onclick = function(event) {
      const ebModal = $('#mySizeChartModal');
      if (event.target === ebModal) {
        closeCartModal();
      }
    };
  }

  // ============================================
  // SOLD OUT STATE INITIALIZATION
  // ============================================

  /**
   * Initialize sold out state for buttons
   * Adds 'sold_out' class if no variants available
   */
  function initializeSoldOutState() {
    console.log('[JL-CORE] Initializing sold out state');

    const oneTimeBtn = $('.one_time_cart_btn');
    const subCartBtn = $('.sub_cart_btn');

    // Check one-time availability
    if (oneTimeBtn) {
      if ($('.onetime')) {
        removeClass(oneTimeBtn, 'sold_out');
        console.log('[JL-CORE] One-time products available');
      } else {
        addClass(oneTimeBtn, 'sold_out');
        console.log('[JL-CORE] One-time products sold out');
      }
    }

    // Check subscription availability
    if (subCartBtn) {
      if ($('.subs')) {
        removeClass(subCartBtn, 'sold_out');
        console.log('[JL-CORE] Subscription products available');
      } else {
        addClass(subCartBtn, 'sold_out');
        console.log('[JL-CORE] Subscription products sold out');
      }
    }
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  /**
   * Initialize the core subscription module
   */
  function init() {
    console.log('[JL-CORE] Initializing core subscription module...');

    // Initial visibility setup
    hide('#div2');
    hide('.sub_cart_area');

    // Sanitize URL (fix duplicate question marks)
    sanitizeUrl();

    // Handle URL variant parameter
    handleUrlVariantParameter();

    // Bind all event handlers
    bindProductBoxHandlers();
    bindTabLinkHandlers();
    bindOneTimeCartButton();
    bindSubscriptionCartButton();
    bindModalHandlers();

    // Initialize sold out state
    initializeSoldOutState();

    // Mark as initialized
    JLSubscriptionCore._initialized = true;

    console.log('[JL-CORE] Core subscription module initialized successfully');
  }

  // ============================================
  // PUBLIC API
  // ============================================

  const JLSubscriptionCore = {
    // Initialization flag
    _initialized: false,

    // Initialization
    init,

    // URL handling
    getUrlVars,
    changeurl,
    sanitizeUrl,

    // Tab switching
    switchToOneTime,
    switchToSubscription,

    // Cart operations
    fetchCart,
    renderCart,
    clearCartDrawer,

    // Modal operations
    showCartModal,
    closeCartModal,

    // Price updates
    updatePriceDisplay,
    updateProductName,

    // Image updates
    updateProductImage,

    // Sold out state
    initializeSoldOutState
  };

  // Export to window for testing
  global.JLSubscriptionCore = JLSubscriptionCore;

  // Auto-initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already loaded
    init();
  }

  console.log('[JL-CORE] Module loaded and exported to window.JLSubscriptionCore');

})(typeof window !== 'undefined' ? window : this);
